import React from "react";
import { NavLink } from "react-router-dom";
import { adminTheme } from "../../components/admin/adminTheme";
import { useAuth } from "../../hooks/useAuth";
import type { StaffSubRole } from "../../auth/roleRoutes";

type SideNavProps = {
  mobileOpen?: boolean;
  onClose?: () => void;
};

type NavItem = {
  to: string;
  label: string;
  end?: boolean;
  visibleFor?: StaffSubRole[];
};

const COMMON_ITEMS: NavItem[] = [
  { to: "/staff/dashboard/overview", label: "Overview" },
  { to: "/staff/dashboard/providers", label: "Providers" },
  { to: "/staff/dashboard/platform-settings", label: "Platform settings" },
  { to: "/staff/dashboard/services/categories", label: "Service categories" },
  { to: "/staff/dashboard/staff", label: "HnB staff" },
  { to: "/staff/dashboard/ai/plans", label: "AI plans" },
  { to: "/staff/dashboard/provider-plans", label: "Provider plans" },
];

const ROLE_SPECIFIC_ITEMS: NavItem[] = [
  {
    to: "/staff/dashboard/md",
    label: "MD/CEO control room",
    visibleFor: ["CEO"],
  },
  {
    to: "/staff/dashboard/hr",
    label: "HR dashboard",
    visibleFor: ["CEO", "HR_HEAD", "HR_MANAGER"],
  },
  {
    to: "/staff/dashboard/hr/workbench",
    label: "HR workbench",
    visibleFor: ["CEO", "HR_HEAD", "HR_MANAGER"],
  },
  {
    to: "/staff/dashboard/hr/onboarding",
    label: "HR onboarding",
    visibleFor: ["CEO", "HR_HEAD", "HR_MANAGER"],
  },
  {
    to: "/staff/dashboard/operations",
    label: "Operations control room",
    visibleFor: ["CEO", "BUSINESS_HEAD", "BDO", "OPERATIONS"],
  },
  {
    to: "/staff/dashboard/audit",
    label: "Audit dashboard",
    visibleFor: ["CEO", "AUDIT_HEAD", "AUDITOR"],
  },
  {
    to: "/staff/dashboard/finance",
    label: "Finance dashboard",
    visibleFor: ["CEO", "FINANCE_HEAD", "FINANCE_OFFICER"],
  },
  {
    to: "/staff/dashboard/cfo",
    label: "CFO dashboard",
    visibleFor: ["CEO", "CFO"],
  },
  {
    to: "/staff/dashboard/accounts",
    label: "Accounts dashboard",
    visibleFor: ["CEO", "ACCOUNTS_HEAD", "ACCOUNTS_OFFICER"],
  },
  {
    to: "/staff/dashboard/it",
    label: "IT dashboard",
    visibleFor: ["CEO", "CTO", "ERP_HEAD", "LLM_HEAD", "IT_COMPLIANCE_HEAD"],
  },
  {
    to: "/staff/dashboard/grievance",
    label: "Grievance & feedback",
    visibleFor: ["CEO", "GRIEVANCE_HEAD", "GRIEVANCE_OFFICER", "SUPPORT"],
  },
];

function canViewItem(
  item: NavItem,
  staffSubRole: StaffSubRole | null | undefined,
): boolean {
  if (!item.visibleFor || item.visibleFor.length === 0) {
    return true;
  }

  if (!staffSubRole) {
    return false;
  }

  return item.visibleFor.includes(staffSubRole);
}

export default function SideNav({
  mobileOpen = false,
  onClose,
}: SideNavProps) {
  const { user } = useAuth();
  const staffSubRole = user?.staffSubRole ?? null;

  const navItems = [...COMMON_ITEMS, ...ROLE_SPECIFIC_ITEMS].filter((item) =>
    canViewItem(item, staffSubRole),
  );

  return (
    <>
      <button
        type="button"
        aria-label="Close dashboard navigation"
        onClick={onClose}
        className={`app-shell-backdrop${mobileOpen ? " is-open" : ""}`}
      />

      <aside
        className={`app-shell-sidenav${mobileOpen ? " is-open" : ""}`}
        aria-label="Dashboard sidebar"
      >
        <div
          style={{
            marginBottom: "18px",
            padding: "0 10px",
          }}
        >
          <div
            style={{
              fontSize: adminTheme.type.eyebrow,
              fontWeight: 800,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              color: adminTheme.color.textFaint,
              marginBottom: "0.45rem",
            }}
          >
            Navigation
          </div>

          <p
            style={{
              margin: 0,
              color: adminTheme.color.textMuted,
              fontSize: "0.92rem",
              lineHeight: 1.6,
            }}
          >
            Internal HnB modules for admin control, ERP oversight, and staff-only
            operations.
          </p>
        </div>

        <nav
          aria-label="Dashboard navigation"
          style={{ display: "flex", flexDirection: "column", gap: "8px" }}
        >
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              onClick={onClose}
              style={({ isActive }) => ({
                textDecoration: "none",
                padding: "12px 14px",
                borderRadius: adminTheme.radius.sm,
                fontWeight: isActive ? 700 : 600,
                color: isActive
                  ? adminTheme.color.text
                  : adminTheme.color.textSoft,
                background: isActive
                  ? adminTheme.color.accentBg
                  : "transparent",
                boxShadow: isActive
                  ? `inset 0 0 0 1px ${adminTheme.color.accentBorder}`
                  : "none",
              })}
            >
              {item.label}
            </NavLink>
          ))}
        </nav>
      </aside>
    </>
  );
}